import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/our-business", label: "Our Business" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled
          ? "border-zinc-200 bg-white/80 backdrop-blur-xl"
          : "border-transparent bg-white"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12 lg:px-16">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="group flex items-center gap-3"
        >
          <div className="flex h-11 w-11 items-center justify-center bg-zinc-950 text-white">
            <span className="font-heading text-lg font-black leading-none">
              A
            </span>
          </div>
          <div className="leading-none">
            <div className="font-heading text-xl font-bold tracking-tight text-zinc-950">
              Agrim Ventures
            </div>
            <div className="mt-1 font-mono-plex text-[11px] uppercase tracking-[0.24em] text-zinc-500">
              Private Limited
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-2 md:flex" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 font-heading text-[15px] font-semibold tracking-tight transition-colors ${
                  isActive
                    ? "text-zinc-950"
                    : "text-zinc-500 hover:text-zinc-950"
                }`
              }
            >
              {({ isActive }) => (
                <span className="flex items-center gap-2">
                  <span
                    className={`h-[7px] w-[7px] ${
                      isActive ? "bg-orange-500" : "bg-transparent"
                    }`}
                  />
                  {item.label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center border border-zinc-300 md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="border-t border-zinc-200 bg-white md:hidden"
          data-testid="mobile-nav"
        >
          <div className="flex flex-col px-6 py-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `border-b border-zinc-100 py-4 font-heading text-lg font-semibold ${
                    isActive ? "text-zinc-950" : "text-zinc-500"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
